import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { ShieldCheck, ShieldOff, ShieldQuestion } from 'lucide-react';
import { db } from '../lib/firebase';

type Channel = 'whatsapp' | 'email' | 'sms' | 'facebook';

interface ConsentDoc {
  optIn?: Partial<Record<Channel, { scope: 'utility' | 'marketing' | 'all'; source: string; grantedAt?: number }>>;
  suppressed?: Partial<Record<Channel, { at: number; reason: string }>>;
  fetchedAt?: number;
}

interface Props {
  contactId: string;
  channel: string;
}

// Subscribes to /contacts_consent/{contactId} and shows the agent the current
// opt-in / suppression state for the chat's channel at a glance.
export function ConsentBadge({ contactId, channel }: Props) {
  const [data, setData] = useState<ConsentDoc | null>(null);
  useEffect(() => {
    if (!contactId) return;
    const unsub = onSnapshot(doc(db, 'contacts_consent', contactId), (snap) => {
      setData(snap.exists() ? (snap.data() as ConsentDoc) : null);
    });
    return unsub;
  }, [contactId]);

  if (channel !== 'whatsapp' && channel !== 'email' && channel !== 'sms' && channel !== 'facebook') return null;
  const ch = channel as Channel;
  const sup = data?.suppressed?.[ch];
  const opt = data?.optIn?.[ch];

  if (sup) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800 border border-red-200"
        title={`Suppressed: ${sup.reason}`}
      >
        <ShieldOff className="w-3 h-3" /> opted out
      </span>
    );
  }
  if (opt) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
        title={`opt-in ${opt.scope} (${opt.source})`}
      >
        <ShieldCheck className="w-3 h-3" /> {opt.scope}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200"
      title="No opt-in recorded — marketing outbound will be blocked"
    >
      <ShieldQuestion className="w-3 h-3" /> consent unknown
    </span>
  );
}
